<script setup lang="ts">
import { Key } from "ts-key-enum";
import { computed, ref, useSlots, warn, watchEffect } from "vue";
import { type DrawerUrlOptions } from "../../../store/drawers";
import { usePermission } from "../../../composables/permission";
import MTooltip from "../MTooltip";
import MTooltipBasic from "../MTooltipBasic";
import { type RouteLocationRaw, useRouter } from "vue-router";

const props = defineProps<{
  href?: string;

  // TODO add type name
  to?: string | { url: string; name?: string; method?: string } | { name: string };
  permission?: string | true | "true" | { url: string; method?: string };

  toModal?: string | DrawerUrlOptions | object | false;
  toSlideover?: string | DrawerUrlOptions | object | false;

  border?: boolean;
  disable?: boolean;
  disabled?: boolean;

  outline?: boolean;
  ghost?: boolean;

  // ENG-20661 — q-btn parity variants
  flat?: boolean; // transparent, no border (Quasar q-btn flat)
  round?: boolean; // circular icon button (Quasar q-btn round)
  fab?: boolean; // circular raised/elevated button (Quasar q-btn fab)

  icon?: string;
  count?: string | number;

  color?: "secondary" | "danger";
  label?: string;
  srLabel?: string;
  // ENG-20949 aria-labelledby as a prop (replaces raw aria-labelledby attrs).
  labelledby?: string;

  click?: (...args: any[]) => PromiseLike<any> | void;
  onClick?: (...args: any[]) => PromiseLike<any> | void;

  loading?: boolean;
  dense?: boolean;
  small?: boolean;

  class?: any;

  borderless?: boolean;

  iconOnly?: boolean;

  tooltip?: string;
}>();

const buttonEl = ref<HTMLButtonElement | HTMLAnchorElement | HTMLElement | null>(null);
const outerEl = ref<HTMLElement | null>(null);

const slots = useSlots();

// eslint-disable-next-line no-undef
if (IS_APP && (import.meta.env.DEV || location.host.startsWith("uat."))) {
  watchEffect(() => {
    // ENG-20949 accessible name comes from a prop now (label / sr-label /
    // labelledby) or slot content — raw aria-label(ledby) attrs no longer checked.
    if (props.label || slots.default || props.srLabel || props.labelledby) return;
    warn('This button is not accessible, please pass a "label" or "sr-label" property');
  });
}

defineEmits({
  click: (...args: any[]) => true,
});

const submitting = ref(false);
const router = useRouter();
const { disabled, hasAccess, onClick } = usePermission(props, false, router, submitting);

const componentType = computed(() => {
  if (props.href) return "a";
  if (disabled.value || !props.to) {
    return "button";
  }
  return "router-link";
});

// ENG-21095: resolve a navigable href so the outer container can be <a>,
// enabling right-click / middle-click / Ctrl+click "Open in new tab".
const resolvedHref = computed<string | undefined>(() => {
  if (disabled.value) return undefined;
  if (props.href) return props.href;
  if (!props.to) return undefined;
  const t = props.to;
  // Custom medicus server URL { url: string, method?: string }
  if (typeof t === "object" && t !== null && "url" in t) return (t as { url: string }).url;
  // Vue Router named/path location
  try {
    return router.resolve(t as RouteLocationRaw).href;
  } catch {
    return undefined;
  }
});

// Outer container is <a> when navigable so right-click hits an anchor element.
const outerTag = computed(() => (resolvedHref.value ? "a" : "div"));
// Inner element becomes <span> when outer is <a> — avoids invalid nested <a>/<button>.
const innerTag = computed(() => (outerTag.value === "a" ? "span" : componentType.value));

const fontAwesomeIcon = computed(() => {
  switch (props.icon) {
    case "add":
      return "fa-solid fa-plus";
    default: {
      return props.icon;
    }
  }
});

const bclass = computed(() => props.class);

async function handleClick(ev: MouseEvent | KeyboardEvent) {
  // When outer <a> is present it owns all click/keyboard navigation.
  if (outerTag.value === "a") return;
  if (submitting.value || disabled.value) return;
  if ("code" in ev && ev.code === Key.Enter) {
    return;
  }
  ev.stopPropagation();
  onClick(ev);
}

// ENG-21095: handles left-click on the outer <a> for SPA navigation.
// Right-click / middle-click / modifier+click fall through to native <a href> behaviour.
async function handleOuterClick(ev: MouseEvent) {
  if (outerTag.value !== "a") return;
  if (ev.button !== 0 || ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey) return;
  if (submitting.value || disabled.value) return;
  ev.stopPropagation();
  const t = props.to;
  const isSpaLink = t && !(typeof t === "object" && "url" in t) && !props.href;
  if (isSpaLink) {
    ev.preventDefault();
    await router.push(t as RouteLocationRaw);
  }
  onClick(ev);
}

function focus() {
  if (outerTag.value === "a") {
    outerEl.value?.focus();
  } else {
    buttonEl.value?.focus();
  }
}

defineExpose({
  focus,
});
</script>
<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<template>
  <component
    :is="outerTag"
    ref="outerEl"
    class="medicus-button-container"
    :href="resolvedHref"
    :color="color"
    :class="[
      !border && !ghost && !flat && 'raised',
      { disable: disabled, ghost, outline, dense, flat, round, fab },
      { 'icon-only small': small || iconOnly },
      bclass,
      { borderless },
    ]"
    v-bind="outerTag === 'a' ? $attrs : undefined"
    @click="handleOuterClick"
  >
    <MTooltipBasic v-if="!hasAccess"
      >You don't have permission to perform this action.</MTooltipBasic
    >
    <component
      :is="innerTag"
      ref="buttonEl"
      class="medicus-button flex-auto medicus-outline"
      :title="iconOnly ? label : null"
      :aria-label="srLabel || undefined"
      :aria-labelledby="labelledby || undefined"
      v-bind="outerTag !== 'a' ? $attrs : undefined"
      :data-testid="outerTag !== 'a' ? ($attrs['data-testid'] as string | undefined) : undefined"
      :disabled="outerTag !== 'a' ? disabled : undefined"
      :type="innerTag === 'button' ? $attrs.type || 'button' : undefined"
      :to="innerTag === 'router-link' ? to : undefined"
      :href="innerTag === 'a' ? href : undefined"
      @click="handleClick"
      @keydown.enter="handleClick"
    >
      <template v-if="icon">
        <i v-if="icon.startsWith('fak')" class="m-mr-sm m-icon" :class="icon" />
        <i v-else ref="iconEl" :class="fontAwesomeIcon" class="m-mr-sm" aria-hidden="true" />
      </template>
      <div v-if="count !== undefined" class="button-count m-mr-sm">
        <span>{{ +count > 99 ? "+99" : count }}</span>
      </div>
      <div
        v-if="ghost"
        class="button-content"
        :class="{ 'sr-only': iconOnly || (srLabel && !label) }"
      >
        <slot>
          <span v-text="label ?? srLabel" />
        </slot>
      </div>
      <slot v-else>
        <span :class="{ 'sr-only': iconOnly || srLabel }">
          {{ label ?? srLabel }}
        </span>
      </slot>
      <m-tooltip v-if="tooltip" :text="tooltip" :scroll-target="buttonEl" />
    </component>
  </component>
</template>

<style lang="scss" scoped>
.medicus-button-container {
  display: block;
  height: 35px;
  min-width: 80px;
  font-weight: bold;
  //width: auto;
  box-sizing: border-box;
  text-decoration: none;
  color: inherit;

  .medicus-button {
    border-radius: 4px;
    padding: 0 12px;
    width: 100%;
    height: 100%;
    border: none;

    background: transparent;

    // text
    font-style: normal;
    font-size: 14px;
    @media (max-width: 768px) {
      line-height: 120%;
    }
    line-height: 150%;
    text-align: center;
    cursor: pointer;
    outline-width: 20px;
    text-decoration: none;
    outline: none;

    &:focus-visible {
      outline: 2px solid var(--focus-ring);
      outline-offset: 2px;
    }

    // display: inline-block;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }
  > a {
    display: flex !important;
    justify-content: center;
    align-items: center;
  }

  // colours
  .medicus-button {
    background: var(--theme-blue);
    color: #ffffff;
    border: 1px solid var(--theme-accent-blue);

    &:hover {
      background: var(--theme-accent-blue);
    }
  }

  &.hover-component .medicus-button,
  .medicus-button:hover {
    background: var(--theme-accent-blue);
  }

  &[color="danger"] {
    .medicus-button {
      font-weight: bold;

      // ENG-20860 set text colour on .medicus-button itself, not just button/a — a link
      // button's inner element is a <span>, which the `button, a` rule below misses, so it
      // fell back to the default white text (invisible on the white background).
      color: var(--status-red);
      border-color: var(--status-red);
      background: #ffffff;
    }
    button,
    a {
      color: var(--status-red);
    }

    &.hover-component .medicus-button,
    .medicus-button:hover {
      background: var(--status-light-red);
      border-color: var(--status-red);
    }

    &.ghost {
      .medicus-button {
        color: var(--status-red);
      }
      &.hover-component .medicus-button,
      .medicus-button:hover {
        background: none;
      }
    }
  }

  &[color="secondary"] {
    .medicus-button {
      font-weight: bold;
      border-color: var(--text-color-light);
      color: var(--text-color);
      background: #ffffff;
    }

    button,
    a {
      color: var(--text-color-light);
    }

    &.hover-component .medicus-button,
    .medicus-button:hover {
      // background: #e7eaee;
      background: var(--grey-light);
    }

    &.ghost {
      .medicus-button {
        color: var(--text-color);
      }
      &.hover-component .medicus-button,
      .medicus-button:hover {
        background: none;
      }
    }
  }

  // &[color].ghost.disabled > .medicus-button,
  &.disable > .medicus-button,
  .disabled,
  [disabled] {
    font-weight: bold;
    background: #ffffff !important;

    border-color: var(--grey) !important;
    color: var(--grey) !important;

    &:hover {
      background: #ffffff !important;

      border-color: var(--grey) !important;
      color: var(--grey) !important;
    }

    .button-content {
      background: #ffffff !important;

      border-color: var(--grey) !important;
      color: var(--grey) !important;

      text-decoration-line: none !important;
    }

    span {
      color: var(--grey) !important;
      text-decoration: none !important;
      text-decoration-line: none !important;
    }
  }

  // /colours

  // ghost

  &.ghost {
    border: none;
    box-shadow: none;

    // prevent size changes
    white-space: nowrap;
    background: none;

    min-width: auto;
    align-self: flex-start;

    height: auto;

    .medicus-button {
      color: var(--theme-blue);

      padding: 0;
      background: none;

      border: none;

      font-weight: bold;
    }

    > a {
      display: inline-block !important;
    }

    .button-content {
      // display: contents;
      text-decoration-line: underline;
      text-underline-offset: 3px;

      &:hover,
      &:focus-within {
        text-decoration-thickness: 3px;
      }
      > span {
        @media (max-width: 769px) {
          white-space: normal !important;
          text-align: center;
        }
      }
    }

    i {
      text-decoration: none !important;
      text-decoration-line: none !important;
      text-decoration-color: white;
    }

    &.disable {
      background-color: transparent !important;

      .button-content,
      .medicus-button {
        background-color: transparent !important;
      }
    }
  }
  // /ghost

  &.dense {
    min-width: auto;
    border-color: transparent !important;
    border-width: 3px !important;

    .medicus-button {
      padding: 5px 12px;
    }

    i,
    svg {
      margin-right: 0;
    }
  }

  &.small {
    min-width: auto;
    align-self: center;
  }

  // ENG-20661 — q-btn parity variants
  // ENG-20661 — Quasar `flat` is transparent + no border, with text colour
  // INHERITED from the surrounding context (light on the dark sidebar, dark on
  // a white page). Don't hardcode a colour or it overrides that inheritance.
  &.flat {
    min-width: auto;

    .medicus-button {
      background: transparent;
      border-color: transparent;
      color: inherit;

      &:hover {
        // neutral overlay — reads on both dark and light backgrounds
        background: rgba(127, 127, 127, 0.15);
      }
    }

    &[color="danger"] .medicus-button {
      color: var(--status-red);
    }

    // disabled flat must stay transparent — the default disabled style paints a
    // white box + grey text, which is wrong on a dark surface (e.g. the sidebar's
    // current-page nav item). Keep it transparent and let colour inherit.
    &.disable > .medicus-button,
    > .medicus-button[disabled] {
      background: transparent !important;
      border-color: transparent !important;
      color: inherit !important;
      opacity: 1 !important; // don't dim — match the enabled nav items

      // the base disabled rule greys the label + content; keep flat inheriting
      span,
      .button-content,
      i,
      svg,
      .m-icon {
        color: inherit !important;
      }

      &:hover {
        background: transparent !important;
      }
    }
  }

  &.round,
  &.fab {
    min-width: 0;

    .medicus-button {
      border-radius: 50%;
      width: 35px;
      height: 35px;
      padding: 0;
      aspect-ratio: 1 / 1;

      &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px var(--focus-ring);
      }
    }

    i,
    svg {
      margin: 0;
    }
  }

  &.fab .medicus-button {
    width: 40px;
    height: 40px;
    box-shadow:
      0 1px 5px rgba(0, 0, 0, 0.2),
      0 2px 2px rgba(0, 0, 0, 0.14);
  }

  &.borderless > .medicus-button {
    border: none;
  }
  &.icon-only {
    min-width: auto;
    &:focus {
      padding: 3px 8px;
    }
    i,
    svg {
      margin: 0;
    }

    button,
    a {
      padding: 5px 10px;
    }
  }

  .button-count {
    background-color: var(--status-light-blue);
    border: 1px solid var(--theme-accent-blue);
    color: var(--theme-blue);

    border-radius: 9999px;
    width: 22px;
    height: 22px;
    font-size: 12px;

    display: flex;
    align-items: center;
    justify-content: center;

    > span {
      padding-top: 2px;
    }
  }

  &.inline {
    display: inline-block;
  }
}
</style>
